import { memo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import Discount from '@/resources/components/discount/Discount';
import BgImage from '@/resources/components/global/Image/BgImage';
import FreeShippingAlert from '@/resources/components/product/freeShippingAlert/FreeShippingAlert';
import Price from '@/resources/components/product/price/Price';
import { useDevice } from '@/resources/hooks/useDevice';
import { useProductLink } from '@/resources/hooks/useProductLink';

import { DesktopComboProduct } from './desktopProduct';
import { COMBO_STYLES, ComboProductProps } from './types';
import { calculateDiscountPrice, getDimensionsByDevice, shouldShowVariations } from './utils';

function ComboProduct({
  product,
  className = '',
  min_quantity,
  discount_percentage = 0,
  variations_count = 0,
}: ComboProductProps) {
  const { title } = product;
  const { device } = useDevice();
  const link = useProductLink({ product });
  const discountPrice = calculateDiscountPrice(product.price, discount_percentage);
  const getComboInfo = () => ({
    title,
    min_quantity,
    variations_count,
    discountPrice,
    link,
  });

  if (device === 'desktop') {
    return (
      <DesktopComboProduct
        product={product}
        info={getComboInfo()}
        dimensions={getDimensionsByDevice(device)}
        className={className}
      />
    );
  }

  const { height, width } = getDimensionsByDevice(device);

  return (
    <div className={`product-one-line-card horizontal-product ${className}`}>
      <Link to={link} className="w-100" style={{ textDecoration: 'none' }}>
        <div className="product-wrapper">
          <div style={{ height, width }}>
            <Container fluid>
              <Row className="d-flex justify-content-between align-items-center">
                <Col
                  xs={10}
                  md={8}
                  className="d-flex flex-row pb-4 pl-0"
                  style={{ height: COMBO_STYLES.MOBILE.containerHeight }}
                >
                  <Col xs={2} md={2} className="my-auto px-0 mr-3">
                    <div className="image-wrapper position-relative">
                      <BgImage className="image" src={product.image} />
                    </div>
                  </Col>

                  <FreeShippingAlert delivery_discount_data={product.delivery_discount_data} />

                  <Col xs={12} md={6} lg={7} className="pr-0">
                    <div className="d-flex flex-column justify-content-between">
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-baseline">
                          <h1 className="combo-title mb-0">{title}</h1>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Col>

                <Discount
                  type="tag"
                  isAbsolute
                  price={discountPrice}
                  defaultPrice={product.price}
                  style={COMBO_STYLES.MOBILE.discountPosition}
                  textStyle={device === 'mobile' ? { display: 'block' } : {}}
                />

                <div
                  className="d-flex flex-column justify-content-end align-items-start"
                  style={COMBO_STYLES.MOBILE.quantityPosition}
                >
                  <span>{min_quantity} un</span>
                  {shouldShowVariations(variations_count) && (
                    <span> +{variations_count} Variações</span>
                  )}
                </div>

                <div style={COMBO_STYLES.MOBILE.pricePosition}>
                  <div className="d-flex d-md-block flex-row-reverse align-items-end">
                    <Price
                      price={discountPrice}
                      defaultPrice={product.price}
                      priceStyles={{ flexDirection: 'column', alignItems: 'flex-end' }}
                    />
                  </div>
                </div>
              </Row>
            </Container>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default memo(ComboProduct);
